import instance from "@johnpaul/jp-api-client";
import { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import { NavigateFunction } from "react-router";

import { getClientCredentials } from "@companion-core/shared/app/Utils/config";
import { useAuthStore } from "@companion-core/shared/app/Store/authStore";
import { isMobile, isWeb } from "@companion-core/shared/app/Utils/platform";
import { useConfigStore } from "@companion-core/shared/app/Store/configStore";
import { useAutoLogoutStore } from "@companion-core/shared/app/Store/autoLogoutStore";

export const handleResponseUnauthorizedError = async (
  navigate: NavigateFunction | (() => void),
  error: AxiosError
): Promise<never> => {
  if (!error?.config?.headers?.["doNotForceExit"]) {
    if (
      useAuthStore.getState().authenticated &&
      !error?.response?.config.url?.includes("/auth/revoke")
    ) {
      await useAuthStore.getState().logout(false);
    }
    // Mobile navigation will automatically redirect to the correct screen
    // When the store value "authenticated" is updated, the conditionnal rendering will do the rest
    if (isWeb()) {
      const ssoModule = useConfigStore.getState().config?.modules.sso;
      if (ssoModule?.enabled) {
        const ssoAuthorizeUrl = ssoModule?.options?.ssoAuthorizeUrl;
        if (ssoAuthorizeUrl) {
          redirectToSsoAuthorize(ssoAuthorizeUrl, window.location.pathname);
        } else {
          navigate("/sso/no-rights");
        }
      } else {
        navigate("login");
      }
    }
  }
  return Promise.reject(error);
};

const redirectToSsoAuthorize = (ssoAuthorizeUrl: string, state: string): void => {
  try {
    const url = new URL(ssoAuthorizeUrl);
    url.searchParams.set("state", state);
    url.searchParams.set("redirect_uri", window.location.origin + "/");
    window.location.href = url.toString();
  } catch {
    const hasParams = ssoAuthorizeUrl.includes("?");
    const separator = hasParams ? "&" : "?";
    const encodedState = encodeURIComponent(state);
    const encodedRedirectUri = encodeURIComponent(window.location.origin + "/");
    window.location.href = `${ssoAuthorizeUrl}${separator}state=${encodedState}&redirect_uri=${encodedRedirectUri}`;
  }
};

export const handleResponseNotFoundError = async (
  navigate: NavigateFunction | (() => void),
  error: AxiosError
): Promise<never> => {
  navigate(isWeb() ? "not-found" : "NotFound");
  return Promise.reject(error);
};

export const handleResponseServerError = async (
  navigate: NavigateFunction | (() => void),
  error: AxiosError
): Promise<never> => {
  navigate(isWeb() ? "error" : "ServerError");
  return Promise.reject(error);
};

const isSSOTokenRoute = (url: string) => !!url?.includes("/auth/token/sso");

const isNotContentOrRessoureRoute = (url: string) => url?.includes("/v2/cross-sell-concierges");

let xClientInterceptorSet = false;

export function setXClientInterceptors() {
  if (xClientInterceptorSet) {
    return;
  }

  instance.interceptors.request.use((request: InternalAxiosRequestConfig) => {
    request.headers["x-client"] = `Basic ${btoa(
      `${getClientCredentials().clientId}:${getClientCredentials().clientSecret}`
    )}`;
    return request;
  });

  xClientInterceptorSet = true;
}

export type ClientPlatform = "web" | "ios" | "android";

export type ClientVersionConfig = {
  /** Version of the JS framework (companion-core), read from the workspace package.json. */
  coreVersion: string;
  /** "web" on the web app, Platform.OS ("ios" | "android") on mobile. */
  platform: ClientPlatform;
  /**
   * Native build version on mobile (DeviceInfo.getVersion(), i.e. CFBundleShortVersionString
   * on iOS / versionName on Android). Omit on web: there is no native build version to send.
   */
  appVersion?: string;
};

let clientVersionInterceptorSet = false;

export function setClientVersionInterceptor({
  coreVersion,
  platform,
  appVersion,
}: ClientVersionConfig) {
  if (clientVersionInterceptorSet) {
    return;
  }

  instance.interceptors.request.use((request: InternalAxiosRequestConfig) => {
    request.headers["X-Companion-Core-Version"] = coreVersion;
    request.headers["X-App-Platform"] = platform;
    if (appVersion) {
      request.headers["X-App-Version"] = appVersion;
    }
    return request;
  });

  clientVersionInterceptorSet = true;
}

let interceptorsSet = false;

export function setInterceptors(
  navigate: NavigateFunction | (() => void),
  networkState?: () => Promise<{ isInternetReachable: boolean | null }>
): void {
  if (interceptorsSet) {
    return;
  }

  instance.interceptors.response.use((response: AxiosResponse) => {
    if (response?.headers?.["x-session-expires-at"]) {
      const sessionExpiresAt = Number(response.headers["x-session-expires-at"]);
      if (sessionExpiresAt > 0) {
        try {
          const rehydrate = useAutoLogoutStore.persist.rehydrate;
          useAutoLogoutStore.getState().initTimeout(sessionExpiresAt, rehydrate);
        } catch {
          // Ignore initTimeout errors to avoid blocking responses
        }
      }
    }
    return response;
  });

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      if (!error.response) {
        return Promise.reject(error);
      }

      const url = error.response.config.url ?? "";
      switch (error.response.status) {
        case 401:
          return handleResponseUnauthorizedError(navigate, error);
        case 403:
          return !isSSOTokenRoute(url)
            ? handleResponseUnauthorizedError(navigate, error)
            : Promise.reject(error);
        case 404:
          if (isNotContentOrRessoureRoute(url)) {
            return Promise.reject(error);
          }
          return handleResponseNotFoundError(navigate, error);
        case 500:
        case 503:
          return !isSSOTokenRoute(url)
            ? handleResponseServerError(navigate, error)
            : Promise.reject(error);
        default:
          return Promise.reject(error);
      }
    }
  );

  if (isMobile() && networkState) {
    // Network check disabled to prevent blocking queries in new architecture
  }

  interceptorsSet = true;
  useConfigStore.getState().setInterceptorLoaded(true);
}
